import React, { useState, useEffect, Component } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { fetchEGWInsightsForVerse, fetchEGWBookInfo, fetchPreviousParagraph, fetchNextParagraph, fetchEGWFullPageContent } from '../../../services/egwService';
import { cleanEGWText } from '../../../utils/textCleaner';
import { EGWPanelSkeleton } from '../../../components/ui/SkeletonLoader';

// ── Error Boundary ────────────────────────────────────────────────────────────
class EGWErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('EGWVerseInsights error boundary caught:', error, info);
  }
  render() {
    if (this.state?.hasError) {
      return (
        <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-600 mb-2">
            <Icon name="AlertCircle" size={15} />
            <span className="font-medium text-sm">EGW panel encountered an error</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => this.setState({ hasError: false, error: null })}
            className="text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
          >
            <Icon name="RefreshCw" size={13} className="mr-1" />
            Retry
          </Button>
        </div>
      );
    }
    return this.props?.children;
  }
}

// Render inline Bible references as clickable links
const renderEGWTextWithRefs = (text, onVerseReference) => {
  if (!text) return null;
  const cleaned = cleanEGWText(text);
  if (!onVerseReference) return cleaned;

  const refPattern = /\b((?:[123]\s)?[A-Z][a-z]+(?:\s[A-Z][a-z]+)?)\s+(\d+):(\d+)(?:-(\d+))?\b/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = refPattern?.exec(cleaned)) !== null) {
    if (match?.index > lastIndex) {
      parts?.push(cleaned?.slice(lastIndex, match?.index));
    }
    const [fullMatch, bookName, chap, startVerse, endVerse] = match;
    const ref = endVerse
      ? `${bookName} ${chap}:${startVerse}-${endVerse}`
      : `${bookName} ${chap}:${startVerse}`;
    parts?.push(
      <button
        key={`egw-ref-${match?.index}`}
        onClick={() => onVerseReference?.(ref)}
        className="text-amber-600 hover:text-amber-800 underline font-medium transition-colors"
        title={`Go to ${ref}`}
      >
        {fullMatch}
      </button>
    );
    lastIndex = match?.index + fullMatch?.length;
  }

  if (lastIndex < cleaned?.length) {
    parts?.push(cleaned?.slice(lastIndex));
  }

  return parts?.length > 0 ? parts : cleaned;
};

const EGWVerseInsightsInner = ({ book, chapter, verse, onClose, onVerseReference, textSizePx }) => {
  const [insights, setInsights] = useState({ scriptureIndex: [], bibleReferences: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);
  const [contentCache, setContentCache] = useState({});
  const [loadingContent, setLoadingContent] = useState({});
  const [showFullPageModal, setShowFullPageModal] = useState(false);
  const [fullPageContent, setFullPageContent] = useState(null);
  const [fullPageLoading, setFullPageLoading] = useState(false);
  const [curatedAccordionOpen, setCuratedAccordionOpen] = useState(false);
  const [directQuotesAccordionOpen, setDirectQuotesAccordionOpen] = useState(false);

  useEffect(() => {
    if (book && chapter && verse) {
      fetchInsights();
    }
  }, [book, chapter, verse]);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      setError(null);
      setExpandedItem(null);
      setContentCache({});

      const data = await fetchEGWInsightsForVerse(book, chapter, verse);

      const allBookCodes = [
        ...new Set([
          ...data?.scriptureIndex?.map(item => item?.egw_book_code),
          ...data?.bibleReferences?.map(item => item?.book_code)
        ])
      ];

      const bookMap = await fetchEGWBookInfo(allBookCodes);

      const enhancedScriptureIndex = data?.scriptureIndex?.map(item => ({
        ...item,
        bookInfo: bookMap?.[item?.egw_book_code] || { title: item?.egw_book_code }
      }));

      const enhancedBibleReferences = data?.bibleReferences?.map(item => ({
        ...item,
        bookInfo: bookMap?.[item?.book_code] || { title: item?.book_code }
      }));

      setInsights({
        scriptureIndex: enhancedScriptureIndex,
        bibleReferences: enhancedBibleReferences
      });
    } catch (err) {
      console.error('Error fetching EGW insights:', err);
      setError(err?.message || 'Failed to load EGW insights');
    } finally {
      setLoading(false);
    }
  };

  const toggleItemExpansion = async (itemId, type) => {
    if (expandedItem === itemId) {
      setExpandedItem(null);
      return;
    }
    setExpandedItem(itemId);
    if (!contentCache?.[itemId]) {
      await fetchContentForItem(itemId, type);
    }
  };

  const fetchContentForItem = async (itemId, type) => {
    setLoadingContent(prev => ({ ...prev, [itemId]: true }));
    try {
      if (type === 'scriptureIndex') {
        const item = insights?.scriptureIndex?.find(ref => `si-${ref?.id}` === itemId);
        if (!item) return;

        const { data: pageContent, error } = await supabase
          ?.from('egw_content')
          ?.select('content_sequence_id, book_code, page_number, paragraph_number, content, toc_title, book_id')
          ?.eq('book_code', item?.egw_book_code)
          ?.eq('page_number', item?.page_start?.toString())
          ?.order('paragraph_number', { ascending: true });

        if (error) throw error;

        if (pageContent && pageContent?.length > 0) {
          setContentCache(prev => ({
            ...prev,
            [itemId]: {
              type: 'pageContent',
              content: pageContent,
              bookId: pageContent?.[0]?.book_id,
              pageNumber: pageContent?.[0]?.page_number
            }
          }));
        }
      } else if (type === 'bibleReference') {
        const item = insights?.bibleReferences?.find(ref =>
          `br-${ref?.book_code}-${ref?.page}-${ref?.paragraph}` === itemId
        );

        if (item?.book_code && item?.page && item?.paragraph) {
          const { data: content, error } = await supabase
            ?.from('egw_content')
            ?.select('content_sequence_id, book_code, book_id, page_number, paragraph_number, content, toc_title')
            ?.eq('book_code', item?.book_code)
            ?.eq('page_number', item?.page?.toString())
            ?.eq('paragraph_number', item?.paragraph?.toString())
            ?.maybeSingle();

          if (error) throw error;

          if (content) {
            setContentCache(prev => ({
              ...prev,
              [itemId]: {
                type: 'paragraph',
                content: [content],
                bookId: content?.book_id,
                pageNumber: content?.page_number
              }
            }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      setContentCache(prev => ({
        ...prev,
        [itemId]: { type: 'error', error: err?.message }
      }));
    } finally {
      setLoadingContent(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleExpandParagraph = async (itemId, direction) => {
    const cached = contentCache?.[itemId];
    if (!cached?.content) return;

    const key = `${itemId}-${direction}`;
    setLoadingContent(prev => ({ ...prev, [key]: true }));

    try {
      const currentContent = cached?.content;
      let newParagraph;

      if (direction === 'before') {
        const firstSequenceId = currentContent?.[0]?.content_sequence_id;
        newParagraph = await fetchPreviousParagraph(firstSequenceId);
        if (newParagraph) {
          setContentCache(prev => ({
            ...prev,
            [itemId]: { ...cached, content: [newParagraph, ...currentContent] }
          }));
        }
      } else if (direction === 'after') {
        const lastSequenceId = currentContent?.[currentContent?.length - 1]?.content_sequence_id;
        newParagraph = await fetchNextParagraph(lastSequenceId);
        if (newParagraph) {
          setContentCache(prev => ({
            ...prev,
            [itemId]: { ...cached, content: [...currentContent, newParagraph] }
          }));
        }
      }
    } catch (err) {
      console.error('Error expanding paragraph:', err);
    } finally {
      setLoadingContent(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleViewFullPage = async (bookId, pageNumber) => {
    setFullPageLoading(true);
    setShowFullPageModal(true);
    try {
      const content = await fetchEGWFullPageContent(bookId, pageNumber);
      setFullPageContent({
        content,
        bookId,
        pageNumber,
        bookCode: content?.[0]?.book_code,
        tocTitle: content?.[0]?.toc_title
      });
    } catch (err) {
      console.error('Error fetching full page:', err);
      setFullPageContent({ error: err?.message });
    } finally {
      setFullPageLoading(false);
    }
  };

  const closeFullPageModal = () => {
    setShowFullPageModal(false);
    setFullPageContent(null);
  };

  const formatReference = (bookCode, page, paragraph) => {
    return `{${bookCode} ${page}.${paragraph}}`;
  };

  const formatHeaderReference = (bookInfo, page) => {
    const title = bookInfo?.title || 'Unknown';
    return `${title} pg ${page}`;
  };

  const hasScriptureIndex = insights?.scriptureIndex?.length > 0;
  const hasBibleReferences = insights?.bibleReferences?.length > 0;

  // Show skeleton while loading
  if (loading) {
    return <EGWPanelSkeleton />;
  }

  return (
    <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-amber-500/10 border-b border-amber-500/20">
        <div className="flex items-center gap-2">
          <Icon name="Sparkles" size={15} className="text-amber-500" />
          <span className="font-semibold text-foreground" style={{ fontSize: textSizePx || '13px' }}>EGW Comments</span>
          <span className="text-muted-foreground" style={{ fontSize: textSizePx ? `calc(${textSizePx} - 2px)` : '11px' }}>
            {book} {chapter}:{verse}
          </span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground h-6 w-6 p-0"
        >
          <Icon name="X" size={14} />
        </Button>
      </div>

      <div className="p-3 space-y-2">
        {/* Error */}
        {error && !loading && (
          <div className="flex items-center gap-2 text-red-600 py-3">
            <Icon name="AlertCircle" size={15} />
            <span style={{ fontSize: textSizePx || '13px' }}>{error}</span>
            <Button size="sm" variant="outline" onClick={fetchInsights} className="ml-auto text-xs">
              Retry
            </Button>
          </div>
        )}

        {/* No results */}
        {!loading && !error && !hasScriptureIndex && !hasBibleReferences && (
          <div className="text-center py-4 text-muted-foreground">
            <Icon name="BookOpen" size={20} className="mx-auto mb-2 opacity-60" />
            <p style={{ fontSize: textSizePx || '13px' }}>No EGW insights found for this verse.</p>
          </div>
        )}

        {/* Curated References Accordion */}
        {!loading && !error && hasScriptureIndex && (
          <div className="space-y-1">
            <button
              onClick={() => setCuratedAccordionOpen(!curatedAccordionOpen)}
              className="w-full flex items-center justify-between p-2 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 bg-amber-500 rounded" />
                <span className="font-semibold text-foreground" style={{ fontSize: textSizePx || '13px' }}>Curated</span>
                <span className="bg-amber-500/20 text-amber-500 px-1.5 py-0.5 rounded font-medium" style={{ fontSize: textSizePx ? `calc(${textSizePx} - 2px)` : '11px' }}>
                  {insights?.scriptureIndex?.length}
                </span>
              </div>
              <Icon
                name={curatedAccordionOpen ? 'ChevronUp' : 'ChevronDown'}
                size={14}
                className="text-amber-500"
              />
            </button>

            {curatedAccordionOpen && (
              <div className="space-y-1 pl-1">
                {insights?.scriptureIndex?.map((item) => {
                  const itemId = `si-${item?.id}`;
                  const isExpanded = expandedItem === itemId;
                  const cached = contentCache?.[itemId];
                  const isLoading = loadingContent?.[itemId];

                  return (
                    <div key={itemId} className="bg-amber-500/5 border border-amber-500/20 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleItemExpansion(itemId, 'scriptureIndex')}
                        className="w-full p-2 flex items-center justify-between hover:bg-amber-500/10 transition-colors text-left"
                      >
                        <span className="font-semibold text-foreground" style={{ fontSize: textSizePx || '13px' }}>
                          {formatHeaderReference(item?.bookInfo, item?.page_start)}
                        </span>
                        <Icon
                          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                          size={13}
                          className="text-amber-500 flex-shrink-0"
                        />
                      </button>

                      {isExpanded && (
                        <div className="px-2 pb-2 border-t border-amber-500/20">
                          {isLoading ? (
                            <div className="flex items-center justify-center py-3 gap-2">
                              <Icon name="Loader" size={13} className="animate-spin text-amber-500" />
                              <span className="text-muted-foreground" style={{ fontSize: textSizePx || '13px' }}>Loading content...</span>
                            </div>
                          ) : cached?.type === 'pageContent' && cached?.content ? (
                            <div className="space-y-2 mt-2">
                              {cached?.content?.map((para) => (
                                <div key={para?.content_sequence_id} className="bg-card p-2 rounded border border-border">
                                  <p className="text-foreground leading-relaxed" style={{ fontSize: textSizePx || '13px', lineHeight: '1.6' }}>
                                    {renderEGWTextWithRefs(para?.content, onVerseReference)}
                                  </p>
                                  <p className="text-muted-foreground mt-1" style={{ fontSize: textSizePx ? `calc(${textSizePx} - 2px)` : '11px' }}>
                                    {formatReference(para?.book_code, para?.page_number, para?.paragraph_number)}
                                  </p>
                                </div>
                              ))}
                              {cached?.bookId && cached?.pageNumber && (
                                <Button
                                  onClick={() => handleViewFullPage(cached?.bookId, cached?.pageNumber)}
                                  variant="outline"
                                  size="sm"
                                  className="w-full mt-1 h-7 text-xs"
                                >
                                  <Icon name="Maximize2" size={11} className="mr-1" />
                                  View Full Page
                                </Button>
                              )}
                            </div>
                          ) : cached?.type === 'error' ? (
                            <p className="text-red-600 py-2" style={{ fontSize: textSizePx || '13px' }}>Error: {cached?.error}</p>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Direct Reference Accordion */}
        {!loading && !error && hasBibleReferences && (
          <div className="space-y-1">
            <button
              onClick={() => setDirectQuotesAccordionOpen(!directQuotesAccordionOpen)}
              className="w-full flex items-center justify-between p-2 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 bg-amber-500 rounded" />
                <span className="font-semibold text-foreground" style={{ fontSize: textSizePx || '13px' }}>Direct Reference</span>
                <span className="bg-amber-500/20 text-amber-500 px-1.5 py-0.5 rounded font-medium" style={{ fontSize: textSizePx ? `calc(${textSizePx} - 2px)` : '11px' }}>
                  {insights?.bibleReferences?.length}
                </span>
              </div>
              <Icon
                name={directQuotesAccordionOpen ? 'ChevronUp' : 'ChevronDown'}
                size={14}
                className="text-amber-500"
              />
            </button>

            {directQuotesAccordionOpen && (
              <div className="space-y-1 pl-1">
                {insights?.bibleReferences?.map((item) => {
                  const itemId = `br-${item?.book_code}-${item?.page}-${item?.paragraph}`;
                  const isExpanded = expandedItem === itemId;
                  const cached = contentCache?.[itemId];
                  const isLoading = loadingContent?.[itemId];

                  return (
                    <div key={itemId} className="bg-amber-500/5 border border-amber-500/20 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleItemExpansion(itemId, 'bibleReference')}
                        className="w-full p-2 flex items-center justify-between hover:bg-amber-500/10 transition-colors text-left"
                      >
                        <span className="font-semibold text-foreground" style={{ fontSize: textSizePx || '13px' }}>
                          {formatHeaderReference(item?.bookInfo, item?.page)}
                        </span>
                        <Icon
                          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                          size={13}
                          className="text-amber-500 flex-shrink-0"
                        />
                      </button>

                      {isExpanded && (
                        <div className="px-2 pb-2 border-t border-amber-500/20">
                          {isLoading ? (
                            <div className="flex items-center justify-center py-3 gap-2">
                              <Icon name="Loader" size={13} className="animate-spin text-amber-500" />
                              <span className="text-muted-foreground" style={{ fontSize: textSizePx || '13px' }}>Loading paragraph...</span>
                            </div>
                          ) : cached?.type === 'paragraph' && cached?.content ? (
                            <div className="space-y-2 mt-2">
                              <Button
                                onClick={() => handleExpandParagraph(itemId, 'before')}
                                variant="ghost"
                                size="sm"
                                className="w-full text-amber-500 hover:bg-amber-500/10 h-7 text-xs"
                                disabled={loadingContent?.[`${itemId}-before`]}
                              >
                                {loadingContent?.[`${itemId}-before`] ? (
                                  <Icon name="Loader" size={11} className="animate-spin mr-1" />
                                ) : (
                                  <Icon name="ChevronUp" size={11} className="mr-1" />
                                )}
                                Load Previous
                              </Button>

                              {cached?.content?.map((para) => (
                                <div key={para?.content_sequence_id} className="bg-card p-2 rounded border border-border">
                                  <p className="text-foreground leading-relaxed" style={{ fontSize: textSizePx || '13px', lineHeight: '1.6' }}>
                                    {renderEGWTextWithRefs(para?.content, onVerseReference)}
                                  </p>
                                  <p className="text-muted-foreground mt-1" style={{ fontSize: textSizePx ? `calc(${textSizePx} - 2px)` : '11px' }}>
                                    {formatReference(para?.book_code, para?.page_number, para?.paragraph_number)}
                                  </p>
                                </div>
                              ))}

                              <Button
                                onClick={() => handleExpandParagraph(itemId, 'after')}
                                variant="ghost"
                                size="sm"
                                className="w-full text-amber-500 hover:bg-amber-500/10 h-7 text-xs"
                                disabled={loadingContent?.[`${itemId}-after`]}
                              >
                                {loadingContent?.[`${itemId}-after`] ? (
                                  <Icon name="Loader" size={11} className="animate-spin mr-1" />
                                ) : (
                                  <Icon name="ChevronDown" size={11} className="mr-1" />
                                )}
                                Load Next
                              </Button>

                              {cached?.bookId && cached?.pageNumber && (
                                <Button
                                  onClick={() => handleViewFullPage(cached?.bookId, cached?.pageNumber)}
                                  variant="outline"
                                  size="sm"
                                  className="w-full mt-1 h-7 text-xs"
                                >
                                  <Icon name="Maximize2" size={11} className="mr-1" />
                                  View Full Page
                                </Button>
                              )}
                            </div>
                          ) : cached?.type === 'error' ? (
                            <p className="text-red-600 py-2" style={{ fontSize: textSizePx || '13px' }}>Error: {cached?.error}</p>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Full Page Modal */}
      {showFullPageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col border border-border">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {fullPageContent?.bookCode} - Page {fullPageContent?.pageNumber}
                </h2>
                {fullPageContent?.tocTitle && (
                  <p className="text-sm text-muted-foreground mt-1">{fullPageContent?.tocTitle}</p>
                )}
              </div>
              <button
                onClick={closeFullPageModal}
                className="p-2 hover:bg-muted rounded-full transition-colors"
              >
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {fullPageLoading ? (
                <div className="flex items-center justify-center py-12 gap-3">
                  <Icon name="Loader" size={24} className="animate-spin text-primary" />
                  <span className="text-muted-foreground">Loading full page...</span>
                </div>
              ) : fullPageContent?.error ? (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <p className="text-red-600">Error: {fullPageContent?.error}</p>
                </div>
              ) : fullPageContent?.content ? (
                <div className="space-y-4">
                  {fullPageContent?.content?.map((para) => (
                    <div key={para?.content_sequence_id} className="bg-muted p-4 rounded-lg border border-border">
                      <p className="text-foreground leading-relaxed" style={{ fontSize: textSizePx || '15px', lineHeight: '1.6' }}>
                        {renderEGWTextWithRefs(para?.content, onVerseReference)}
                      </p>
                      <p className="text-muted-foreground mt-2" style={{ fontSize: textSizePx ? `calc(${textSizePx} - 2px)` : '11px' }}>
                        {formatReference(para?.book_code, para?.page_number, para?.paragraph_number)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="p-4 border-t border-border flex justify-end">
              <Button onClick={closeFullPageModal} variant="outline">Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrap with error boundary
const EGWVerseInsights = (props) => (
  <EGWErrorBoundary>
    <EGWVerseInsightsInner {...props} />
  </EGWErrorBoundary>
);

export default EGWVerseInsights;
